import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import axios from "./axios";

/**
 * Sante du parcours d'accueil.
 *
 * POURQUOI CET ECRAN EXISTE
 * -------------------------
 * Deux clients sont restes bloques dehors la meme semaine. Dans les deux cas
 * l'information etait en base des la premiere minute ; il manquait simplement
 * un endroit pour la regarder. Le diagnostic a demande une session SSH, un
 * client MySQL et cinq requetes ecrites a la main sur la production, et la
 * reparation un INSERT direct dans `company_user`.
 *
 * Ces cinq requetes sont derriere `useOnboardingIssues`. L'INSERT est derriere
 * `useAttachUser`. Ce n'est pas un ecran de confort : c'est la difference entre
 * "l'exploitant repare en deux clics" et "le developpeur ouvre une transaction
 * sur la prod un vendredi soir".
 *
 * TOUT EST GARDE sur `fleet.user.manage` cote API. La clef est deja semee (elle
 * protege deja les ecrans Utilisateurs et Societes), donc l'ecran est visible
 * des le deploiement — contrairement a une clef neuve, qui rendrait la page
 * invisible pour tout le monde jusqu'au passage du seeder.
 */

// -- Types -------------------------------------------------------------------

/** Compte actif qui n'ouvre aucune societe. */
export interface OrphanUser {
  user_id: number;
  email: string;
  name: string | null;
  created_at: string;
  /**
   * Un compte non verifie est presque toujours une inscription abandonnee ; un
   * compte verifie sans rattachement est une anomalie a traiter. C'est le seul
   * critere qui permet de trancher sans appeler la personne.
   */
  email_verified: boolean;
}

/**
 * Invitation en attente alors que le compte existe DEJA et est actif.
 *
 * C'est l'impasse exacte de BA Conseil : l'ecran d'invitation affichait "vous
 * avez deja un compte, connectez-vous", la connexion ne rattachait rien, et
 * personne ne pouvait en sortir par l'interface. L'API corrige la cause (le
 * rattachement a lieu desormais a la connexion) ; cette liste sert a voir les
 * cas deja crees, et ceux qu'un futur chemin de creation ferait reapparaitre.
 */
export interface StuckInvitation {
  invitation_code: string;
  company_id: number;
  company_name: string | null;
  email: string;
  role: "FLEET_MANAGER" | "EMPLOYEE" | string;
  user_id: number;
  invited_at: string;
  expires_at: string;
  is_expired: boolean;
}

/** Invitation jamais ouverte, fenetre de 72 h depassee, statut reste `pending`. */
export interface ExpiredInvitation {
  invitation_code: string;
  company_id: number;
  company_name: string | null;
  email: string;
  role: string;
  invited_at: string;
  expires_at: string;
}

/** Societe de flotte active que personne ne peut administrer. */
export interface FleetWithoutManager {
  company_id: number;
  company_name: string;
  created_at: string;
  pending_invitations: number;
}

/**
 * Compte sans mot de passe choisi par son proprietaire, et sans societe.
 *
 * Residu des pre-comptes que l'API creait pour chaque invitation FLEET_MANAGER
 * avant d'arreter de le faire. La personne n'a jamais rien fait de ce compte,
 * mais il occupe SON adresse. Tant qu'il est `is_active`, l'ecran d'invitation
 * le prenait pour un compte utilisable et renvoyait son proprietaire vers une
 * page de connexion sans identifiants -- l'impasse d'origine.
 *
 * Jamais supprime automatiquement : la sortie est un rattachement depuis cet
 * ecran quand la personne est legitime, une suppression manuelle sinon.
 */
export interface GhostAccount {
  user_id: number;
  email: string;
  name: string | null;
  /** Vrai = cas grave : actif, donc pris pour un compte utilisable. */
  is_active: boolean;
  created_at: string;
}

export interface OnboardingIssues {
  orphan_users: OrphanUser[];
  stuck_invitations: StuckInvitation[];
  expired_invitations: ExpiredInvitation[];
  fleets_without_manager: FleetWithoutManager[];
  /** Optionnel : absent si l'Admin est deploye devant une API plus ancienne. */
  ghost_accounts?: GhostAccount[];
}

export interface OnboardingSummary {
  orphan_users: number;
  stuck_invitations: number;
  expired_invitations: number;
  fleets_without_manager: number;
  ghost_accounts?: number;
}

export const onboardingKeys = {
  all: () => ["onboarding"] as const,
  issues: () => [...onboardingKeys.all(), "issues"] as const,
  summary: () => [...onboardingKeys.all(), "summary"] as const,
};

// -- Lecture -----------------------------------------------------------------

/**
 * Les quatre categories en un seul appel.
 *
 * UNE requete, pas quatre. L'exploitant ouvre cet ecran pour savoir "est-ce que
 * quelque chose est casse", pas pour interroger une categorie precise. Quatre
 * appels seraient quatre etats de chargement pour une seule question, et quatre
 * occasions d'afficher une page a moitie vraie.
 */
export const useOnboardingIssues = () => {
  const { data, ...res } = useQuery({
    queryKey: onboardingKeys.issues(),
    queryFn: async (): Promise<{
      issues: OnboardingIssues;
      summary: OnboardingSummary;
    }> => {
      const response = await axios.get("/admin/onboarding/issues");
      return response.data;
    },
    /*
     * Ces chiffres bougent a chaque connexion d'utilisateur. Les servir depuis
     * le cache pendant plusieurs minutes ferait afficher "3 invitations
     * bloquees" a un exploitant qui vient d'en reparer trois — et lui ferait
     * douter de l'ecran, ce qui est pire que de ne pas l'avoir.
     */
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  return {
    issues: data?.issues,
    summary: data?.summary,
    ...res,
  };
};

/**
 * Compteurs seuls, pour une pastille de menu.
 *
 * Separe de useOnboardingIssues pour qu'un badge affiche en permanence ne tire
 * pas le detail des quatre listes a chaque navigation.
 */
export const useOnboardingSummary = (enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: onboardingKeys.summary(),
    queryFn: async (): Promise<OnboardingSummary & { total: number }> => {
      const response = await axios.get("/admin/onboarding/summary");
      return response.data;
    },
    enabled,
    staleTime: 60_000,
  });

  return { summary: data, ...res };
};

// -- Actions -----------------------------------------------------------------

export interface AttachUserRequest {
  user_id: number;
  company_id: number;
  role: "FLEET_MANAGER" | "EMPLOYEE";
}

export interface AttachUserResult {
  company_user_id: number;
  user_id: number;
  email: string;
  company_id: number;
  company_name: string;
  company_role: string;
  /** Vrai si le compte etait inactif et vient d'etre active par ce rattachement. */
  user_activated: boolean;
  invitations_closed: number;
}

/**
 * Rattacher un compte a une societe.
 *
 * L'action qui a manque pendant tout l'incident : le compte existait, la
 * societe existait, l'invitation existait, et il n'y avait aucun moyen de
 * relier les trois autrement qu'en INSERT direct.
 *
 * Idempotent cote API : rattacher deux fois reactive et aligne le role plutot
 * que de creer un doublon.
 */
export const useAttachUser = (onDone?: (result: AttachUserResult) => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AttachUserRequest): Promise<AttachUserResult> => {
      const response = await axios.post("/admin/onboarding/attach", payload);
      return response.data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all() });
      /*
       * Le message dit ce qu'il reste a faire, pas seulement que ca a marche.
       *
       * Un compte qui vient d'etre active n'a pas forcement de mot de passe
       * connu de son proprietaire : sur un pre-compte d'invitation, le mot de
       * passe est le code d'invitation, que personne n'a jamais vu. Sans cette
       * phrase l'exploitant croit avoir termine, et la personne reste dehors.
       */
      toast.success(
        result.user_activated
          ? t(
              "onboarding.attachedAndActivated",
              "Rattaché et compte activé. Demandez-lui de passer par « mot de passe oublié »."
            )
          : t("onboarding.attached", "Rattaché à {{company}}", {
              company: result.company_name,
            })
      );
      onDone?.(result);
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("common.error", "Something went wrong")
      );
    },
  });
};

/**
 * Relancer une invitation, y compris expiree.
 *
 * L'API accepte une invitation depassee (elle ne refuse que `accepted` et
 * `revoked`) : elle renouvelle la fenetre de 72 h, journalise et renvoie le
 * mail. C'est la reparation du cas Climacer, ou l'invitation etait morte depuis
 * cinq jours sans que rien ne le signale.
 */
export const useResendInvitation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      companyId: number;
      invitationCode: string;
    }) => {
      const response = await axios.post(
        `/admin/onboarding/companies/${payload.companyId}/invitations/${payload.invitationCode}/resend`
      );
      return response.data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all() });
      toast.success(
        t("onboarding.resent", "Invitation renvoyée à {{email}}", {
          email: data?.email ?? "",
        })
      );
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("common.error", "Something went wrong")
      );
    },
  });
};

/**
 * Basculer en `expired` toutes les invitations dont la fenetre est passee.
 *
 * Le meme travail que la tache planifiee `onboarding:doctor`, declenchable a la
 * main quand un exploitant veut nettoyer la liste sans attendre le passage de
 * 7 h. Sans effet sur la securite (une invitation depassee etait deja refusee),
 * mais decisif pour la lisibilite : tant que le statut reste `pending`, rien ne
 * distingue une invitation morte d'une invitation vivante.
 */
export const useExpireStaleInvitations = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await axios.post(
        "/admin/onboarding/invitations/expire-stale"
      );
      return response.data as { expired: number };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all() });
      toast.success(
        t("onboarding.expired", "{{count}} invitation(s) marquée(s) expirée(s)", {
          count: data?.expired ?? 0,
        })
      );
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("common.error", "Something went wrong")
      );
    },
  });
};
